import { GrotalisLogo, GrotalisMark } from "@/components/brand/Logo";

export const SectionHead = ({ n, title, text, dark }) => (
  <div className="mb-10 max-w-2xl">
    <p className="text-xs mb-3" style={{ color: dark ? "var(--g-gold)" : "var(--g-burgundy)" }}>{String(n).padStart(2, "0")} · Proposal</p>
    <h2 className="g-serif text-3xl sm:text-4xl lg:text-5xl font-medium" style={{ color: dark ? "var(--g-ivory)" : "var(--g-obsidian)" }}>{title}</h2>
    {text && <p className="mt-3 text-sm sm:text-base leading-relaxed" style={{ color: dark ? "var(--g-stone)" : "#5C5E58" }}>{text}</p>}
  </div>
);

export const Hero = () => (
  <section className="relative overflow-hidden" style={{ background: "var(--g-obsidian)" }} data-testid="brand-hero">
    <img src="/brand/vault-arches.jpg" alt="Stone arches of a historic cellar" className="absolute inset-0 w-full h-full object-cover opacity-35" />
    <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #171C1A 30%, rgba(23,28,26,.55) 100%)" }} />
    <div className="relative max-w-6xl mx-auto px-6 sm:px-10 py-8">
      <div className="flex items-center justify-between">
        <GrotalisLogo size={34} />
        <span className="text-xs px-3 py-1 rounded-full border" style={{ color: "var(--g-champagne)", borderColor: "rgba(198,167,110,.4)" }} data-testid="brand-proposal-badge">Brand proposal · for review</span>
      </div>
      <div className="py-24 sm:py-32 max-w-2xl g-rise">
        <GrotalisMark size={72} />
        <h1 className="g-serif mt-8 text-5xl sm:text-6xl lg:text-7xl font-medium" style={{ color: "var(--g-ivory)", letterSpacing: "0.12em" }} data-testid="brand-name">GROTALIS</h1>
        <p className="mt-3 text-base sm:text-lg" style={{ color: "var(--g-gold)" }}>The Private Cellar</p>
        <p className="g-serif mt-10 text-2xl sm:text-3xl italic" style={{ color: "var(--g-ivory)" }} data-testid="brand-tagline">Where Every Bottle Is a Treasure.</p>
        <div className="mt-10 flex flex-wrap gap-x-10 gap-y-2 text-sm" style={{ color: "var(--g-stone)" }}>
          <span>Preserve the History of Every Bottle.</span>
          <span>Your Collection. Your Legacy.</span>
        </div>
        <nav className="mt-14 flex flex-wrap gap-2" data-testid="brand-section-nav">
          {[["palette", "Colours"], ["logos", "Logo"], ["dashboard", "Dashboard"], ["mobile", "Mobile"], ["label", "Label"]].map(([id, l]) => (
            <a key={id} href={`#${id}`} className="g-btn g-btn-gold" data-testid={`brand-nav-${id}`}>{l}</a>
          ))}
        </nav>
      </div>
    </div>
  </section>
);

const PRIMARY = [["Obsidian Black", "#171C1A", "Main background, navigation", "#F5F1E8"], ["Antique Gold", "#C6A76E", "Logo, icons, premium accents", "#171C1A"], ["Warm Ivory", "#F5F1E8", "Light backgrounds, content", "#171C1A"], ["Deep Forest Green", "#26352E", "Secondary backgrounds, cards", "#F5F1E8"], ["Charcoal", "#303330", "Text, borders", "#F5F1E8"]];
const SUPPORT = [["Champagne Gold", "#D9C29A"], ["Stone Grey", "#A6A49C"], ["Burgundy", "#633C42"], ["Soft White", "#FFFFFF"]];
const FUNC = [["Success", "#3D765B"], ["Warning", "#B78336"], ["Error", "#A44848"], ["Information", "#486B7C"]];

const Chip = ([n, h]) => (
  <div key={n} className="flex items-center gap-3" data-testid={`swatch-${h.slice(1)}`}>
    <span className="w-10 h-10 rounded-md border" style={{ background: h, borderColor: "var(--g-border)" }} />
    <span><span className="block text-sm font-medium">{n}</span><span className="block text-xs" style={{ color: "#6E6F69" }}>{h}</span></span>
  </div>
);

export const Palette = () => (
  <section id="palette" className="max-w-6xl mx-auto px-6 sm:px-10 py-24" data-testid="brand-palette">
    <SectionHead n={1} title="Colour palette" text="Dark, quiet surfaces for the private cellar; warm ivory for the records. Gold marks the brand and a few chosen details only." />
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {PRIMARY.map(([n, h, u, t]) => (
        <div key={h} className="rounded-xl p-5 h-48 flex flex-col justify-between border" style={{ background: h, color: t, borderColor: "var(--g-border)" }} data-testid={`swatch-${h.slice(1)}`}>
          <span className="text-xs opacity-80">{h}</span>
          <span><span className="g-serif block text-xl font-semibold">{n}</span><span className="block text-xs mt-1 opacity-80">{u}</span></span>
        </div>
      ))}
    </div>
    <div className="grid md:grid-cols-2 gap-10 mt-12">
      <div><p className="text-sm font-medium mb-4">Supporting</p><div className="grid grid-cols-2 gap-4">{SUPPORT.map(Chip)}</div></div>
      <div><p className="text-sm font-medium mb-4">System feedback only</p><div className="grid grid-cols-2 gap-4">{FUNC.map(Chip)}</div></div>
    </div>
    <div className="grid md:grid-cols-2 gap-6 mt-12">
      <div className="g-card p-6">
        <p className="g-serif text-3xl" style={{ color: "var(--g-obsidian)" }}>Cormorant Garamond</p>
        <p className="text-sm mt-1" style={{ color: "#6E6F69" }}>Brand, headings, editorial messages</p>
        <p className="g-serif text-xl mt-4 italic">Preserve the History of Every Bottle.</p>
      </div>
      <div className="g-card p-6">
        <p className="text-2xl font-semibold" style={{ color: "var(--g-obsidian)" }}>Inter</p>
        <p className="text-sm mt-1" style={{ color: "#6E6F69" }}>Navigation, forms, tables, buttons, labels</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button className="g-btn g-btn-primary" data-testid="demo-btn-primary">Register sale</button>
          <button className="g-btn g-btn-secondary" data-testid="demo-btn-secondary">Change location</button>
          <button className="g-btn g-btn-gold" style={{ color: "#8A6F3E", borderColor: "#C6A76E" }} data-testid="demo-btn-gold">Collection value</button>
        </div>
      </div>
    </div>
  </section>
);

const LogoTile = ({ name, bg, fg, sub, children, id }) => (
  <div className="rounded-xl border p-6 flex flex-col" style={{ background: bg, borderColor: "var(--g-border)" }} data-testid={`logo-${id}`}>
    <div className="flex-1 flex items-center justify-center min-h-[130px]">{children || <GrotalisLogo color={fg} sub={sub} size={40} />}</div>
    <p className="text-xs mt-4" style={{ color: sub }}>{name}</p>
  </div>
);

export const Logos = () => (
  <section id="logos" style={{ background: "var(--g-white)" }} data-testid="brand-logos">
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-24">
      <SectionHead n={2} title="Logo — the hidden entrance" text="A stone arch with its keystone and a keyhole at the threshold: a secret entrance protecting a valuable collection. Symmetrical, a few strong lines, readable down to a favicon." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <LogoTile id="primary" name="Primary · Antique Gold on Obsidian Black" bg="#171C1A" fg="#C6A76E" sub="#A6A49C" />
        <LogoTile id="secondary" name="Secondary · Obsidian Black on Warm Ivory" bg="#F5F1E8" fg="#171C1A" sub="#6E6F69" />
        <LogoTile id="mono-black" name="Monochrome black" bg="#FFFFFF" fg="#000000" sub="#555555" />
        <LogoTile id="mono-white" name="Monochrome white" bg="#26352E" fg="#FFFFFF" sub="#D5D8D3" />
        <LogoTile id="symbol" name="Symbol · app icon and favicon" bg="#F5F1E8" sub="#6E6F69">
          <div className="flex items-end gap-5">
            <span className="rounded-2xl p-3" style={{ background: "#171C1A" }}><GrotalisMark size={64} bold /></span>
            <span className="rounded-lg p-1.5" style={{ background: "#171C1A" }}><GrotalisMark size={32} bold /></span>
            <span className="rounded p-0.5" style={{ background: "#171C1A" }}><GrotalisMark size={16} bold /></span>
          </div>
        </LogoTile>
        <LogoTile id="horizontal" name="Horizontal · website header" bg="#171C1A" sub="#A6A49C">
          <div className="w-full rounded-md px-4 py-3 flex items-center justify-between" style={{ background: "#26352E" }}>
            <GrotalisLogo size={28} showSub={false} />
            <span className="text-xs whitespace-nowrap hidden xl:inline" style={{ color: "#A6A49C" }}>Collection · Cellar · Scan</span>
          </div>
        </LogoTile>
      </div>
    </div>
  </section>
);
